import { useMemo, useRef, useState } from "react";
import { trpc } from "../../../lib/trpc";
import { Icon } from "../../ui/Icon";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { ToastStack, type ToastItem } from "../../ui/Toast";
import { TesisPickerField, useTesisPicker, resolveTesisId } from "../../ui/TesisPickerField";
import { startPointerDrag } from "../../../lib/dragPointer";
import { OrgChart } from "../OrgChart";
import { computeShiftOff } from "../orgLayout";
import { OrgUnitFormModal, type OrgUnitType } from "../OrgUnitFormModal";
import { ORG_TYPE_META, type OrgUnitRow } from "../orgTypes";
import { useIsMobile } from "../../../lib/useIsMobile";
import { MCard } from "../../mobile/primitives";

type Zone = "before" | "inside" | "after";

/** Organizasyon Şeması sekmesi (İK Faz 1) — birim ağacı + sürükle-bırak + SVG şema. */
export function OrgTab() {
  const qc = trpc.useUtils();
  const isMobile = useIsMobile();
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const perms = meQ.data?.user?.permissions ?? [];
  const isSuper = !!meQ.data?.user?.isSuperAdmin;
  const canManage = isSuper || perms.includes("manage:org");

  const [view, setView] = useState<"tree" | "chart">("tree");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [form, setForm] = useState<{ mode: "new"; parentId: string | null } | { mode: "edit"; unit: OrgUnitRow } | null>(null);
  const [delTarget, setDelTarget] = useState<OrgUnitRow | null>(null);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const toastSeq = useRef(0);
  const pushToast = (t: Omit<ToastItem, "id">) => setToasts((p) => [...p.slice(-4), { ...t, id: String(++toastSeq.current) }]);

  const tp = useTesisPicker(true);
  const [newTesis, setNewTesis] = useState("");

  const listQ = trpc.hr.orgUnit.list.useQuery(undefined, { staleTime: 5_000 });
  const units = useMemo(() => (listQ.data ?? []) as OrgUnitRow[], [listQ.data]);
  const unitsRef = useRef(units); unitsRef.current = units;

  // Vardiya dışı ETKİN küme (miraslı) — hesap `orgLayout.ts`te; baskı belgesi de AYNI
  // fonksiyonu çağırıyor. Kopyalansaydı ekran ile kâğıt zamanla ayrışırdı.
  const shiftOff = useMemo(() => computeShiftOff(units), [units]);

  const invalidate = () => qc.hr.orgUnit.invalidate();
  const createMut = trpc.hr.orgUnit.create.useMutation({
    onSuccess: () => { invalidate(); setForm(null); setNewTesis(""); pushToast({ tone: "success", message: "Birim eklendi." }); },
    onError: (e) => pushToast({ tone: "danger", title: "Eklenemedi", message: e.message }),
  });
  const updateMut = trpc.hr.orgUnit.update.useMutation({
    onSuccess: () => { invalidate(); setForm(null); pushToast({ tone: "success", message: "Birim güncellendi." }); },
    onError: (e) => pushToast({ tone: "danger", title: "Güncellenemedi", message: e.message }),
  });
  const moveMut = trpc.hr.orgUnit.move.useMutation({
    onSuccess: invalidate,
    onError: (e) => pushToast({ tone: "danger", title: "Taşınamadı", message: e.message }),
  });
  const removeMut = trpc.hr.orgUnit.remove.useMutation({
    onSuccess: () => { invalidate(); setDelTarget(null); pushToast({ tone: "success", message: "Birim silindi." }); },
    onError: (e) => pushToast({ tone: "danger", title: "Silinemedi", message: e.message }),
  });

  const visibleRows = useMemo(() => {
    const byId = new Map(units.map((u) => [u.id, u]));
    return units.filter((u) => {
      let pid = u.parentId;
      while (pid) { if (!expanded.has(pid)) return false; pid = byId.get(pid)?.parentId ?? null; }
      return true;
    });
  }, [units, expanded]);

  function toggleExpand(id: string) {
    setExpanded((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }
  function expandAll() { setExpanded(new Set(units.map((u) => u.id))); }

  const dragIdRef = useRef<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropHint, setDropHint] = useState<{ id: string; zone: Zone } | null>(null);
  const dropHintRef = useRef<{ id: string; zone: Zone } | null>(null);

  function dropFromPoint(x: number, y: number): { id: string; zone: Zone } | null {
    const el = document.elementFromPoint(x, y)?.closest(".org-row") as HTMLElement | null;
    const id = el?.dataset.id;
    if (!el || !id || id === dragIdRef.current) return null;
    const srcId = dragIdRef.current;
    if (srcId) {
      const byId = new Map(unitsRef.current.map((u) => [u.id, u]));
      const src = byId.get(srcId), tgt = byId.get(id);
      if (src && tgt && tgt.path.startsWith(src.path)) return null;
    }
    const r = el.getBoundingClientRect();
    const rel = (y - r.top) / r.height;
    const zone: Zone = rel < 0.3 ? "before" : rel > 0.7 ? "after" : "inside";
    return { id, zone };
  }

  function onRowMouseDown(e: React.MouseEvent, id: string) {
    if (!canManage) return;
    startPointerDrag(e, {
      ignoreSelectors: [".org-acts", ".org-twist"],
      dragClass: "org-dragging",
      onStart: () => { dragIdRef.current = id; setDragId(id); },
      onMove: (x, y) => { const d = dropFromPoint(x, y); dropHintRef.current = d; setDropHint(d); },
      onEnd: (_x, _y, moved) => {
        const d = dropHintRef.current, src = dragIdRef.current;
        dragIdRef.current = null; setDragId(null); dropHintRef.current = null; setDropHint(null);
        if (!moved || !d || !src) return;
        const byId = new Map(unitsRef.current.map((u) => [u.id, u]));
        const target = byId.get(d.id); if (!target) return;
        if (d.zone === "inside") moveMut.mutate({ id: src, parentId: d.id });
        else if (d.zone === "before") moveMut.mutate({ id: src, parentId: target.parentId, beforeId: d.id });
        else moveMut.mutate({ id: src, parentId: target.parentId, afterId: d.id });
      },
    });
  }

  function submitForm(data: { name: string; type: OrgUnitType; isShiftBased: boolean }) {
    if (!form) return;
    if (form.mode === "new") {
      createMut.mutate({ name: data.name, type: data.type, parentId: form.parentId, isShiftBased: data.isShiftBased, tesisId: form.parentId ? undefined : resolveTesisId(tp.show, newTesis) });
    } else {
      updateMut.mutate({ id: form.unit.id, name: data.name, type: data.type, isShiftBased: data.isShiftBased });
    }
  }

  const rootAddDisabled = tp.show && !newTesis;

  /**
   * PDF → AYRI BASKI SAYFASI (/print/organizasyon-semasi), şemanın kendisi DEĞİL.
   * Ekrandaki şema tek parça bir SVG; tek parça SVG sayfalara BÖLÜNEMEZ, tarayıcı ya
   * tek sayfaya sıkıştırır (yazı okunmaz olur) ya da altını kırpar. Baskı sayfası
   * girintili hiyerarşi olarak akar → kaç sayfa sürerse sürsün yazı tam boyda kalır.
   * Depodaki diğer /print/* belgeleriyle aynı desen.
   */
  const openPrint = () => window.open("/print/organizasyon-semasi", "_blank", "noopener");

  const drawers = (
    <>
      <OrgUnitFormModal
        open={!!form}
        title={form?.mode === "edit" ? "Birimi Düzenle" : form?.parentId ? "Alt Birim Ekle" : "Yeni Birim"}
        initial={form?.mode === "edit" ? { name: form.unit.name, type: form.unit.type, isShiftBased: form.unit.isShiftBased } : null}
        showShiftToggle
        busy={createMut.isPending || updateMut.isPending}
        onClose={() => setForm(null)}
        onSubmit={submitForm}
      />

      <ConfirmDialog
        open={!!delTarget}
        title="Birimi sil"
        message={delTarget ? `"${delTarget.name}" birimi silinecek. Bu işlem geri alınamaz.` : ""}
        confirmLabel="Sil"
        tone="danger"
        busy={removeMut.isPending}
        onCancel={() => setDelTarget(null)}
        onConfirm={() => delTarget && removeMut.mutate({ id: delTarget.id })}
      />

      <ToastStack toasts={toasts} onDismiss={(id) => setToasts((p) => p.filter((t) => t.id !== id))} />
    </>
  );

  if (isMobile) {
    return (
      <>
        <div className="m-stickytop">
          {tp.show && canManage ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span className="m-ovl">Yeni kök birim tesisi</span>
              <TesisPickerField show={tp.show} facilities={tp.facilities} value={newTesis} onChange={setNewTesis} />
            </div>
          ) : null}
          {canManage ? (
            <button type="button" className="btn-md primary" style={{ justifyContent: "center" }} disabled={rootAddDisabled} onClick={() => setForm({ mode: "new", parentId: null })}>
              <Icon name="plus" size={14} />Birim Ekle
            </button>
          ) : null}
          <button type="button" className="btn-md secondary" style={{ justifyContent: "center" }} onClick={openPrint} disabled={units.length === 0}>
            <Icon name="printer" size={14} />PDF / Yazdır
          </button>
          <div className="m-chips">
            <button type="button" className={`m-chip${view === "tree" ? " act" : ""}`} onClick={() => setView("tree")}>Ağaç Listesi</button>
            <button type="button" className={`m-chip${view === "chart" ? " act" : ""}`} onClick={() => setView("chart")}>Organizasyon Şeması</button>
          </div>
        </div>

        {listQ.isError ? (
          <div className="m-empty">Organizasyon yüklenemedi: {listQ.error.message}</div>
        ) : listQ.isLoading ? (
          <div className="m-empty">Yükleniyor…</div>
        ) : units.length === 0 ? (
          <div className="m-empty">{canManage ? "Henüz organizasyon birimi yok — \"Birim Ekle\" ile en üst birimi oluşturun." : "Görüntülenecek birim bulunmuyor."}</div>
        ) : view === "chart" ? (
          // Kaydırmayı ŞEMANIN KENDİ kutusu yapar (.org-chart-scroll). Buraya ikinci bir
          // `overflow` koymak iç içe kaydırma üretiyordu: telefonda parmak hangi kutuyu
          // sürükleyeceğine karar veremiyor, şema yerine sayfa kayıyordu.
          <div className="org-chart-mwrap">
            <OrgChart units={units} onSelect={(u) => canManage && setForm({ mode: "edit", unit: u })} />
          </div>
        ) : (
          <>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" className="btn-md secondary sm" onClick={expandAll}><Icon name="chevronDown" size={13} />Tümünü aç</button>
              <button type="button" className="btn-md secondary sm" onClick={() => setExpanded(new Set())}><Icon name="chevronUp" size={13} />Tümünü kapat</button>
            </div>
            {visibleRows.map((u) => {
              const meta = ORG_TYPE_META[u.type];
              const hasKids = u.childCount > 0;
              const isOpen = expanded.has(u.id);
              return (
                <MCard pad key={u.id} style={{ marginLeft: u.depth * 14, ...(u.isActive ? {} : { opacity: 0.6 }) }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {hasKids ? (
                      <button type="button" className="m-iconbtn" style={{ width: 30, height: 30, flexShrink: 0, color: "var(--neutral-500)" }} aria-label={isOpen ? "Kapat" : "Aç"} onClick={() => toggleExpand(u.id)}>
                        <Icon name={isOpen ? "chevronDown" : "chevRight"} size={16} />
                      </button>
                    ) : (
                      <span style={{ width: 30, flexShrink: 0 }} aria-hidden="true" />
                    )}
                    <span style={{ color: "var(--primary)", flexShrink: 0, display: "inline-flex" }} aria-hidden="true"><Icon name={meta.icon} size={16} /></span>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 700, color: "var(--neutral-900)" }}>{u.name}</span>
                    <span className="badge b-info no-dot" style={{ flexShrink: 0 }}>{meta.label}</span>
                  </div>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 6, paddingLeft: 38, fontSize: 11.5, color: "var(--neutral-600)" }}>
                    {u.code ? <span className="mono">{u.code}</span> : null}
                    <span>{u.tesisName ?? "Global"}</span>
                    {hasKids ? <span><span style={{ color: "var(--neutral-400)" }}>Alt birim </span>{u.childCount}</span> : null}
                    {!u.isActive ? <span style={{ color: "var(--danger)" }}>Pasif</span> : null}
                    {shiftOff.has(u.id) ? <span style={{ color: "var(--warning)" }}>{shiftOff.get(u.id) === "self" ? "Vardiya dışı" : "Vardiya dışı (üstten)"}</span> : null}
                  </div>
                  {canManage ? (
                    <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "flex-end" }}>
                      <button type="button" className="m-iconbtn m-li-act" aria-label={`${u.name}: alt birim ekle`} onClick={() => { setExpanded((p) => new Set(p).add(u.id)); setForm({ mode: "new", parentId: u.id }); }}><Icon name="plus" size={16} /></button>
                      <button type="button" className="m-iconbtn m-li-act" aria-label={`${u.name}: düzenle`} onClick={() => setForm({ mode: "edit", unit: u })}><Icon name="edit" size={16} /></button>
                      <button type="button" className="m-iconbtn m-li-act danger" aria-label={`${u.name}: sil`} onClick={() => setDelTarget(u)}><Icon name="trash" size={16} /></button>
                    </div>
                  ) : null}
                </MCard>
              );
            })}
            <div style={{ textAlign: "center", fontSize: 12, color: "var(--neutral-400)" }}>{units.length} birim</div>
          </>
        )}

        {drawers}
      </>
    );
  }

  return (
    <div className="hr-tab-body">
      <div className="org-toolbar">
        <div className="seg" role="group" aria-label="Görünüm">
          <button type="button" className={`seg-btn ${view === "tree" ? "active" : ""}`} onClick={() => setView("tree")}>
            <Icon name="list2" size={14} />Ağaç Listesi
          </button>
          <button type="button" className={`seg-btn ${view === "chart" ? "active" : ""}`} onClick={() => setView("chart")}>
            <Icon name="chart" size={14} />Organizasyon Şeması
          </button>
        </div>
        <div style={{ flex: 1 }} />
        <button type="button" className="btn-md secondary" onClick={openPrint} disabled={units.length === 0}
          title="Organizasyon şemasını A4 belge olarak yeni sekmede aç (Yazdır → PDF olarak kaydet)">
          <Icon name="printer" size={14} />PDF / Yazdır
        </button>
        {canManage ? (
          <button type="button" className="btn-md primary" disabled={rootAddDisabled} onClick={() => setForm({ mode: "new", parentId: null })}>
            <Icon name="plus" size={14} />Birim Ekle
          </button>
        ) : null}
      </div>

      {tp.show && canManage ? (
        <div className="org-tesis-pick">
          <span className="lbl">Yeni kök birim tesisi:</span>
          <div style={{ minWidth: 200 }}>
            <TesisPickerField show={tp.show} facilities={tp.facilities} value={newTesis} onChange={setNewTesis} />
          </div>
          <span className="help">Kök birim eklerken bu tesise (ya da Global) yazılır; alt birimler üst birimin tesisini alır.</span>
        </div>
      ) : null}

      <div className="d-card">
        <div className="d-card-body">
          {listQ.isError ? (
            <div className="form-banner-err">Organizasyon yüklenemedi: {listQ.error.message}</div>
          ) : listQ.isLoading ? (
            <p className="td-empty">Yükleniyor…</p>
          ) : units.length === 0 ? (
            <div className="empty-state">
              <span className="ic" aria-hidden="true"><Icon name="chart" size={30} /></span>
              <p className="ttl">Henüz organizasyon birimi yok</p>
              <p className="sub">{canManage ? "\"Birim Ekle\" ile en üst birimi (örn. Genel Müdürlük) oluşturun, sonra altına sürükleyerek ağacı kurun." : "Görüntülenecek birim bulunmuyor."}</p>
            </div>
          ) : view === "chart" ? (
            <OrgChart units={units} onSelect={(u) => canManage && setForm({ mode: "edit", unit: u })} />
          ) : (
            <>
              <div className="org-toolbar">
                <button type="button" className="link-btn" onClick={expandAll}><Icon name="chevronDown" size={12} />Tümünü aç</button>
                <button type="button" className="link-btn" onClick={() => setExpanded(new Set())}><Icon name="chevronUp" size={12} />Tümünü kapat</button>
                {canManage ? <span className="org-hint">İpucu: bir birimi başka birimin üzerine sürükleyerek altına taşıyın.</span> : null}
              </div>
              <ul className="org-tree">
                {visibleRows.map((u) => {
                  const meta = ORG_TYPE_META[u.type];
                  const hasKids = u.childCount > 0;
                  const isOpen = expanded.has(u.id);
                  const hint = dropHint?.id === u.id ? dropHint.zone : null;
                  return (
                    <li key={u.id}
                      className={`org-row ${dragId === u.id ? "is-drag" : ""} ${hint ? `drop-${hint}` : ""} ${!u.isActive ? "inactive" : ""}`}
                      data-id={u.id}
                      style={{ paddingLeft: 8 + u.depth * 22 }}
                      onMouseDown={(e) => onRowMouseDown(e, u.id)}>
                      {hasKids ? (
                        <button type="button" className="org-twist" aria-label={isOpen ? "Kapat" : "Aç"} onClick={() => toggleExpand(u.id)}>
                          <Icon name={isOpen ? "chevronDown" : "chevRight"} size={13} />
                        </button>
                      ) : <span className="org-twist-spacer" />}
                      <span className="org-ic" aria-hidden="true"><Icon name={meta.icon} size={15} /></span>
                      <span className="org-name">{u.name}</span>
                      <span className={`badge-pill b-info org-type`}>{meta.label}</span>
                      {shiftOff.has(u.id) ? (
                        <span className="badge-pill b-warn org-type"
                          title={shiftOff.get(u.id) === "self"
                            ? "Bu birim ve alt birimleri vardiya planında görünmez"
                            : "Üst birimi vardiya dışı olduğu için bu birim de planda görünmez"}>
                          <Icon name="clock" size={11} />
                          {shiftOff.get(u.id) === "self" ? "Vardiya dışı" : "Vardiya dışı (üstten)"}
                        </span>
                      ) : null}
                      {u.code ? <span className="org-code mono">{u.code}</span> : null}
                      {u.tesisName ? <span className="org-tesis">{u.tesisName}</span> : <span className="org-tesis global">Global</span>}
                      {canManage ? (
                        <span className="org-acts">
                          <button type="button" className="iact" title="Alt birim ekle" aria-label={`${u.name}: alt birim ekle`}
                            onClick={() => { setExpanded((p) => new Set(p).add(u.id)); setForm({ mode: "new", parentId: u.id }); }}>
                            <Icon name="plus" size={14} />
                          </button>
                          <button type="button" className="iact" title="Düzenle" aria-label={`${u.name}: düzenle`}
                            onClick={() => setForm({ mode: "edit", unit: u })}><Icon name="edit" size={14} /></button>
                          <button type="button" className="iact danger" title="Sil" aria-label={`${u.name}: sil`}
                            onClick={() => setDelTarget(u)}><Icon name="trash" size={14} /></button>
                        </span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
              <div className="dt2-foot"><span>{units.length} birim</span></div>
            </>
          )}
        </div>
      </div>

      {drawers}
    </div>
  );
}
